import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, useFetch } from "@/lib/api";
import { fmtDate, fmtDateTime, gbp } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, Loading, ErrorBox, EmptyState, Section, StatusBadge, Detail } from "@/components/Common";
import { ActivityList } from "@/components/Activity";
import { PhotoGrid } from "@/components/PhotoGrid";

export function RequestDetail() {
  const { id } = useParams();
  const { data: r, loading, error, reload } = useFetch(`/customer/requests/${id}`);
  const [reply, setReply] = useState("");
  if (loading) return <Loading />;
  if (error) return <ErrorBox error={error} />;
  const send = async () => {
    try { await api.post(`/customer/requests/${id}/reply`, { message: reply }); setReply(""); toast.success("Your reply has been sent to DJ Landscapes"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const active = r.appointments.find((a) => ["Requested", "Confirmed", "Rescheduling Requested"].includes(a.status));
  const open = !["Declined", "Closed", "Converted to Job"].includes(r.status);
  return (
    <div className="space-y-5">
      <PageHeader title={r.reference} subtitle={`Submitted ${fmtDateTime(r.created_at)}`}
        actions={<><StatusBadge status={r.status} customer testId="request-status" />
          {open && !active && <Button asChild size="sm" data-testid="request-book-appt-btn"><Link to={`/portal/appointments/book?request=${r.id}`}>Request a consultation</Link></Button>}</>} />
      {r.status === "Awaiting Customer Information" && (
        <Section title="DJ Landscapes needs more information" testId="info-request-box">
          <p className="mb-2 text-sm text-muted-foreground">See the latest message in the activity below, then reply here.</p>
          <Textarea aria-label="Your reply" value={reply} onChange={(e) => setReply(e.target.value)} data-testid="reply-input" />
          <Button className="mt-2" size="sm" onClick={send} disabled={reply.trim().length < 2} data-testid="reply-send-btn">Send reply</Button>
        </Section>
      )}
      <div className="grid gap-5 lg:grid-cols-3">
        <Section title="Project" className="lg:col-span-2">
          <dl className="grid gap-4 sm:grid-cols-2">
            <Detail label="Services">{r.services.map((s) => s.name).join(", ")}</Detail>
            <Detail label="Property">{r.property.address}, {r.property.postcode}</Detail>
            <Detail label="Budget">{r.budget || "Not given"}</Detail><Detail label="Preferred start">{r.timeframe || "Flexible"}</Detail>
            <Detail label="Area">{r.area}</Detail><Detail label="Deadline">{r.deadline}</Detail>
          </dl>
          <div className="mt-4"><Detail label="Description"><span className="whitespace-pre-wrap">{r.description}</span></Detail></div>
          <h3 className="mb-2 mt-5 text-sm font-semibold">Photographs</h3>
          <PhotoGrid photos={r.photos} />
        </Section>
        <div className="space-y-5">
          <Section title="Appointments">
            {r.appointments.length ? r.appointments.map((a) => (
              <div key={a.id} className="mb-2 flex items-center justify-between rounded-md border border-border p-3 text-sm">
                <span>{a.type}<br /><span className="text-xs text-muted-foreground">{fmtDate(a.date)} {a.start}</span></span><StatusBadge status={a.status} />
              </div>)) : <p className="text-sm text-muted-foreground">None yet.</p>}
          </Section>
          <Section title="Quotations">
            {r.quotations.length ? r.quotations.map((q) => (
              <Link key={q.id} to={`/portal/quotations/${q.id}`} className="mb-2 flex items-center justify-between rounded-md border border-border p-3 text-sm hover:bg-muted">
                <span>{q.reference}<br /><span className="text-xs text-muted-foreground">{gbp(q.total)}</span></span><StatusBadge status={q.status} />
              </Link>)) : <p className="text-sm text-muted-foreground">No quotation issued yet.</p>}
          </Section>
          {r.jobs.map((j) => (
            <Section key={j.id} title="Job"><Link to={`/portal/jobs/${j.id}`} className="flex items-center justify-between text-sm underline">{j.reference}<StatusBadge status={j.status} /></Link></Section>
          ))}
        </div>
      </div>
      <Section title="Activity"><ActivityList items={r.activity} /></Section>
    </div>
  );
}

export function History() {
  const { data, loading, error } = useFetch("/customer/dashboard");
  if (loading) return <Loading />;
  if (error) return <ErrorBox error={error} />;
  return (
    <div className="space-y-6">
      <PageHeader title="Booking and job history" />
      <Section title="Job requests" testId="history-requests">
        {!data.requests.length ? <EmptyState title="No requests yet" action={<Button asChild><Link to="/portal/new">Start a request</Link></Button>} /> : (
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground"><tr><th className="py-2 pr-3">Reference</th><th className="py-2 pr-3">Services</th><th className="py-2 pr-3">Submitted</th><th className="py-2">Status</th></tr></thead>
            <tbody>{data.requests.map((r) => (
              <tr key={r.id} className="border-t border-border">
                <td className="py-2 pr-3"><Link className="font-medium text-primary underline" to={`/portal/requests/${r.id}`} data-testid={`history-request-${r.reference}`}>{r.reference}</Link></td>
                <td className="py-2 pr-3">{r.services.map((s) => s.name).join(", ")}</td><td className="py-2 pr-3">{fmtDate(r.created_at)}</td><td className="py-2"><StatusBadge status={r.status} customer /></td>
              </tr>))}</tbody>
          </table></div>)}
      </Section>
      <Section title="Jobs" testId="history-jobs">
        {!data.jobs.length ? <p className="text-sm text-muted-foreground">No jobs yet. Jobs appear here once a quotation has been accepted and work is booked.</p> : (
          <div className="space-y-2">{data.jobs.map((j) => (
            <Link key={j.id} to={`/portal/jobs/${j.id}`} data-testid={`history-job-${j.reference}`} className="flex items-center justify-between rounded-md border border-border p-3 text-sm hover:bg-muted">
              <span><span className="font-medium">{j.reference}</span><br /><span className="text-xs text-muted-foreground">{j.property_address}</span></span><StatusBadge status={j.status} />
            </Link>))}</div>)}
      </Section>
    </div>
  );
}

export function JobDetail() {
  const { id } = useParams();
  const { data: j, loading, error } = useFetch(`/customer/jobs/${id}`);
  if (loading) return <Loading />;
  if (error) return <ErrorBox error={error} />;
  return (
    <div className="space-y-5">
      <PageHeader title={`Job ${j.reference}`} subtitle={`From request ${j.request_reference}`} actions={<StatusBadge status={j.status} testId="job-status" />} />
      <Section title="Details">
        <dl className="grid gap-4 sm:grid-cols-2">
          <Detail label="Property">{j.property_address}</Detail><Detail label="Agreed price">{gbp(j.agreed_price)}</Detail>
          <Detail label="Planned start">{fmtDate(j.planned_start)}</Detail><Detail label="Expected completion">{fmtDate(j.expected_completion)}</Detail>
          <Detail label="Quotation">{j.quotation_reference}</Detail>
        </dl>
        <div className="mt-4"><Detail label="Work"><span className="whitespace-pre-wrap">{j.description}</span></Detail></div>
      </Section>
      <Section title="Progress"><ActivityList items={j.activity} /></Section>
    </div>
  );
}
